import { Autocomplete, Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import {
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import { useLocation, useNavigate } from 'react-router-dom'
import { URLS } from 'components/shop-reception/mobile/constants'
import { useMemo, useState } from 'react'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { useFormik } from 'formik'
import AlertModal from 'components/shop-reception/mobile/Common/AlertModal/AlertModal'
import { useOfflineElementTool } from 'components/core-ui/Offline'
import { getInitialValues, markOptions, reasonOptions } from './helpers'

const ExpectedTrucksStep4 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const truckId = location.state.truckId
  const supportId = location.state.supportId

  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [isMandatorySupportModalOpen, setIsMandatorySupportModalOpen] = useState(false)
  const [packageWithDifference, setPackageWithDifference] = useState([])

  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'EXPECTED_TRUCKS_STEP_4',
    url: URLS.AUTH_EXPECTED_TRUCKS_STEP4,
    elementId: `${truckId}-${supportId}`
  })

  const { data: hubName, isLoading: isLoadingHubName } = useQuery(
    ['hub-name'],
    async () => {
      const response = await makeCoreGetRequest(`/shop-reception-mobile/home/hub-name`)
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: typeOptions, isLoading: isTypeOptionsLoading } = useQuery(
    ['type-options'],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/principal/type`
      )

      return response
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const confirmList = async (values: any) => {
    await makeCorePostRequest(
      `/shop-reception-mobile/support/shop/save/list/true/${truckId}/${supportId}`,
      values.typeOptions
    )
  }

  const { isLoading: isMutateLoading, mutate } = useMutation(confirmList, {
    onSuccess: async () => {
      showNotification({
        message: `Confirmat`,
        type: 'success'
      })
      await deleteOfflineData()
      navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP3, { state: { item: truckId } })
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(
    () => getInitialValues(typeOptions, offlineData?.data),
    [typeOptions, offlineData?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    onSubmit: async (values: any) => {
      mutate(values)
    }
  })

  const saveSupportTypes = () => {
    const { total, supportsNotOk } = formik.values?.typeOptions.reduce(
      (acc, support) => {
        const total = +support.defect + +support.conform
        acc.total += total
        const supportNotOk = formik.values?.typeOptions.find(
          st => st.id === support.id && +st.received !== total
        )
        if (supportNotOk) {
          acc.supportsNotOk.push(supportNotOk.name)
        }
        return acc
      },
      { total: 0, supportsNotOk: [] }
    )

    if (total === 0) {
      setIsMandatorySupportModalOpen(true)
    } else if (supportsNotOk.length) {
      setPackageWithDifference(supportsNotOk.join(', '))
      setIsAlertOpen(true)
    } else {
      formik.handleSubmit()
    }
  }

  if (isLoadingHubName || isTypeOptionsLoading) {
    return (
      <div style={{ display: 'flex' }}>
        <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
      </div>
    )
  }

  console.log(formik.values)

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      <Grid xs={12} item>
        <Typography
          component="h2"
          variant="medium"
          style={{ textAlign: 'center', textTransform: 'uppercase' }}
          pt={2}
        >
          {hubName.value}
        </Typography>
      </Grid>

      <Grid xs={10} item>
        <Table>
          <TableHead style={{ backgroundColor: '#0970E6' }}>
            <TableRow>
              <TableCell>Nume</TableCell>
              <TableCell>Calitativi</TableCell>
              <TableCell>Defecti</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formik.values?.typeOptions?.map((type, index) => (
              <>
                <TableRow>
                  <TableCell>{type.name}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      variant="outlined"
                      width="70px"
                      value={type.conform}
                      onChange={async event => {
                        formik.setFieldValue(
                          `typeOptions[${index}].conform`,
                          (event.target as HTMLInputElement).value
                        )
                        await saveOfflineData([
                          {
                            field: `offlineTypeOptions[${index}].conform`,
                            value: (event.target as HTMLInputElement).value
                          }
                        ])
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      variant="outlined"
                      width="70px"
                      value={type.defect}
                      onChange={async event => {
                        formik.setFieldValue(
                          `typeOptions[${index}].defect`,
                          (event.target as HTMLInputElement).value
                        )
                        await saveOfflineData([
                          {
                            field: `offlineTypeOptions[${index}].defect`,
                            value: (event.target as HTMLInputElement).value
                          }
                        ])
                      }}
                    />
                  </TableCell>
                </TableRow>
                {!!type.defect && (
                  <Grid container>
                    <Grid xs={10} item>
                      <Autocomplete
                        variant="outlined"
                        label="Motiv"
                        disableClearable
                        value={reasonOptions.find(option => formik.values?.typeOptions[index].reason === option.value)}
                        options={reasonOptions}
                        onChange={async (event, option) => {
                          formik.setFieldValue(`typeOptions[${index}].reason`, option.value)
                          await saveOfflineData([
                            {
                              field: `offlineTypeOptions[${index}].reason`,
                              value: option.value
                            }
                          ])
                        }}
                      />
                    </Grid>
                    <Grid xs={10} item>
                      <Autocomplete
                        variant="outlined"
                        label="Marca"
                        disableClearable
                        value={markOptions.find(option => formik.values?.typeOptions[index].mark === option.value)}
                        options={markOptions}
                        onChange={async (event, option) => {
                          formik.setFieldValue(`typeOptions[${index}].mark`, option.value)
                          await saveOfflineData([
                            {
                              field: `offlineTypeOptions[${index}].mark`,
                              value: option.value
                            }
                          ])
                        }}
                      />
                    </Grid>
                    <Grid xs={10} item>
                      <ImageUpload
                        uploadUrl={`/shop-reception-mobile/support/shop/upload/auxiliary/${truckId}/${type.id}`}
                        onSuccess={() => {
                          showNotification({
                            message: `Imaginea a fost incarcata cu succes.`,
                            type: 'success'
                          })
                        }}
                        onError={() =>
                          showNotification({
                            message: `A aparut o eroare la incarcarea imaginii.`,
                            type: 'error'
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </Grid>
      <Grid xs={10} item>
        <Button
          variant="contained"
          label="Confirma ambalaje"
          onClick={() => {
            saveSupportTypes()
          }}
          style={{ width: '100%' }}
          disabled={isMutateLoading}
        />
      </Grid>
      <AlertModal
        open={isMandatorySupportModalOpen}
        handleClose={() => setIsMandatorySupportModalOpen(false)}
        title="Atenție!"
        description="Tipul de suport este obligatoriu!"
        buttons={[
          {
            label: 'Ok',
            onClick: () => setIsMandatorySupportModalOpen(false),
            color: 'primary'
          }
        ]}
      />
      <AlertModal
        open={isAlertOpen}
        handleClose={() => setIsAlertOpen(false)}
        title={'Atentie'}
        description={`Exista diferente intre cantitatea asteptata si cea descarcata pentru ${packageWithDifference}!`}
        buttons={[
          { label: 'Mai verific', onClick: () => setIsAlertOpen(false), color: 'primary' },
          { label: 'Am terminat', onClick: () => formik.handleSubmit(), color: 'primary' }
        ]}
      />
    </Grid>
  )
}

export default ExpectedTrucksStep4
